

















import { defineComponent, ref, set } from "@vue/composition-api";
import { Participant, ParticipantStatus } from "@scrile/api-provider/dist/api/ParticipantProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useUserVerification from "@/hooks/useUserVerification";
import providers from "@/lib/api";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner";
import ConsentFormModal from "./components/ConsentFormModal.vue";
import RejectParticipation from "./components/RejectParticipation.vue";

export default defineComponent({
  name: "PageParticipantConsent",
  components: { RejectParticipation, BaseSpinner, ConsentFormModal, BasePage },
  props: {
    participantId: { type: String },
    urlUuid: { type: String },
  },
  setup(props, { root }) {
    const { start } = useUserVerification();
    const { authUser, isMember } = useAuthUser();
    const loading = ref(true);
    const showRejectModal = ref(false);
    const showConsentModal = ref(false);
    const participant = ref<Participant | null>(null);
    const owner = ref<UserPublicData | null>(null);
    const submits = ref<Record<string, () => Promise<any>>>({});

    const setCb = ({ field, cb }: { field: string; cb: () => Promise<any> }) => {
      set(submits.value, field, cb);
    };

    const isInvitedUser = (p: Participant) => {
      if (p.userId) return p.userId === authUser.value?.id;
      // приглашённый по email (userId пуст) открывает форму только по ссылке с urlUuid
      return !!props.urlUuid;
    };

    const isConsentAvailable = (p: Participant) =>
      p.status === ParticipantStatus.NEW && !!p.postOwnerId && isInvitedUser(p);

    const getParticipant = async () => {
      if (!props.participantId && !props.urlUuid) {
        root.$router.replace({ name: "home" });
        return;
      }
      const response = await providers.ParticipantProvider.search({
        data: props.urlUuid ? { urlUuid: props.urlUuid } : { id: props.participantId },
      });
      const found = response[0] ?? null;
      if (!found || !isConsentAvailable(found)) {
        root.$router.replace({ name: "home" });
        return;
      }
      participant.value = found;
    };

    const getOwner = async () => {
      if (!participant.value?.postOwnerId) return;
      const response = await providers.UserPublicDataProvider.search({
        data: { id: [participant.value.postOwnerId] },
      });
      owner.value = response.result[0] ?? null;
    };

    const getData = async () => {
      try {
        await getParticipant();
        await getOwner();

        showConsentModal.value = true;
      } finally {
        loading.value = false;
      }
    };

    const onReject = async (cb?: () => void) => {
      if (!participant.value) return;
      await providers.ParticipantProvider.reject({
        data: props.urlUuid ? { urlUuid: props.urlUuid, id: participant.value.id } : { id: participant.value.id },
      });
      cb?.();
      showRejectModal.value = false;
      root.$router.replace({ name: "home" });
    };

    const onSubmit = async (cb?: () => void) => {
      if (!participant.value || !authUser.value) return;

      if (isMember.value) {
        // Backend rejects start() until the personal info submit verifies the phone
        await Promise.all(Object.values(submits.value).map(c => c()));
        await start(authUser.value.id);
      }
      await providers.ParticipantProvider.approve({
        data: props.urlUuid ? { urlUuid: props.urlUuid, id: participant.value.id } : { id: participant.value.id },
      });

      cb?.();
      root.$router.push({ name: "home" });
    };

    const onRejectClose = () => {
      showRejectModal.value = false;
    };

    getData();

    return {
      loading,
      authUser,
      participant,
      owner,
      showConsentModal,
      showRejectModal,
      setCb,
      onReject,
      onSubmit,
      onRejectClose,
    };
  },
});
